import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import QRCodeLib from 'qrcode'
import { HttpProblemError, hasRole, parseSchemaRef, type IngestEnvelope } from '@collector/shared'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { formatDateTime } from '../lib/format'
import { logError } from '../lib/log'
import { encodeEnvelopeForQr } from '../lib/qr'
import { condenseTcccEnvelopeForQr, isTcccSchema } from '../lib/tccc'
import { DataSetVisualizer } from '../components/DataSetVisualizer'
import { PageHeading } from '../components/PageHeading'
import { TcccCardView } from '../components/TcccCardView'

export function DataSetDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const auth = useAuth()
  // Matches the API's `@Roles('admin', 'developer')` on DELETE /data-sets/:id.
  // Users who cannot use the action never see the button.
  const canDelete = hasRole(auth.user, 'admin', 'developer')

  const dataSet = useQuery({
    queryKey: ['data-set', id],
    queryFn: () => api().dataSets.get(id!),
    enabled: Boolean(id),
  })

  const ref = dataSet.data ? parseSchemaRef(dataSet.data.schema) : null
  const schema = useQuery({
    queryKey: ['schema', ref?.name, ref?.version],
    queryFn: () => api().schemas.get(ref!.name, ref!.version),
    enabled: Boolean(ref),
  })

  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const deleteMutation = useMutation({
    mutationFn: (dataSetId: string) => api().dataSets.delete(dataSetId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['data-sets'] })
      void queryClient.invalidateQueries({ queryKey: ['data-set', id] })
      void navigate('/data-sets', { replace: true })
    },
    onError: (err: unknown) => {
      const message =
        err instanceof HttpProblemError
          ? (err.problem.detail ?? err.problem.title)
          : t('dataSetDetail.deleteFailed')
      setDeleteError(message)
      logError('data-set.delete failed', err, { dataSetId: id })
    },
  })

  if (dataSet.isLoading)
    return <p className="text-sm text-stone-500 dark:text-stone-400">{t('common.loading')}</p>
  if (!dataSet.data)
    return (
      <p className="text-sm text-stone-500 dark:text-stone-400">{t('dataSetDetail.notFound')}</p>
    )
  if (schema.isLoading || !schema.data)
    return (
      <p className="text-sm text-stone-500 dark:text-stone-400">
        {t('dataSetDetail.loadingSchema')}
      </p>
    )

  // Reconstruct an envelope for the visualiser and QR export. Data sets
  // don't carry the original tablet UID anymore (the FK was deliberately
  // dropped so revoked tablets can be deleted), so we synthesise a
  // name-based placeholder that still satisfies IngestEnvelopeSchema's
  // min-length requirement when round-tripped through /decode-label.
  const syntheticUid = `name:${dataSet.data.tabletName}`.padEnd(8, '-')
  const envelope: IngestEnvelope = {
    schema: dataSet.data.schema,
    tabletUid: syntheticUid,
    capturedAt: dataSet.data.capturedAt,
    patient: dataSet.data.patient,
    data: dataSet.data.data,
  }
  const isTccc = isTcccSchema(envelope.schema)
  // TCCC cards carry a vitals time series that would blow the QR size
  // budget; condense to the latest reading per measurement for the QR
  // payload. Non-TCCC envelopes pass through unchanged.
  const qrEnvelope = isTccc ? condenseTcccEnvelopeForQr(envelope) : envelope

  return (
    <div>
      <PageHeading
        title={dataSet.data.patient.name}
        description={t('dataSetDetail.capturedOn', {
          tablet: dataSet.data.tabletName,
          time: formatDateTime(dataSet.data.capturedAt),
        })}
        actions={
          <>
            <ExportQrButton envelope={qrEnvelope} />
            {canDelete ? (
              <button
                className="btn-danger"
                onClick={() => {
                  setDeleteError(null)
                  setConfirmDelete(true)
                }}
              >
                {t('common.delete')}
              </button>
            ) : null}
          </>
        }
      />
      {deleteError ? (
        <div className="mb-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-400">
          {deleteError}
        </div>
      ) : null}
      {isTccc ? (
        <TcccCardView envelope={envelope} />
      ) : (
        <DataSetVisualizer envelope={envelope} schema={schema.data.jsonSchema} mode="view" />
      )}

      {confirmDelete ? (
        <ConfirmDeleteDialog
          patientName={dataSet.data.patient.name}
          capturedAt={dataSet.data.capturedAt}
          inFlight={deleteMutation.isPending}
          onCancel={() => {
            setConfirmDelete(false)
          }}
          onConfirm={() => {
            if (!id) return
            deleteMutation.mutate(id, {
              onSettled: () => {
                setConfirmDelete(false)
              },
            })
          }}
        />
      ) : null}
    </div>
  )
}

interface ConfirmDeleteDialogProps {
  patientName: string
  capturedAt: string
  inFlight: boolean
  onCancel: () => void
  onConfirm: () => void
}

function ConfirmDeleteDialog({
  patientName,
  capturedAt,
  inFlight,
  onCancel,
  onConfirm,
}: ConfirmDeleteDialogProps) {
  const { t } = useTranslation()
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-dataset-title"
      className="fixed inset-0 z-10 flex items-center justify-center bg-black/40 px-4"
    >
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-stone-900">
        <h2
          id="delete-dataset-title"
          className="text-lg font-semibold text-stone-900 dark:text-stone-100"
        >
          {t('dataSetDetail.confirmDeleteTitle')}
        </h2>
        <p className="mt-2 text-sm text-stone-600 dark:text-stone-400">
          {t('dataSetDetail.confirmDeleteBody', {
            patientName,
            capturedAt: formatDateTime(capturedAt),
          })}
        </p>
        <div className="mt-6 flex justify-end gap-2">
          <button className="btn-secondary" onClick={onCancel} disabled={inFlight}>
            {t('common.cancel')}
          </button>
          <button className="btn-danger" onClick={onConfirm} disabled={inFlight}>
            {inFlight ? t('dataSetDetail.deleting') : t('common.delete')}
          </button>
        </div>
      </div>
    </div>
  )
}

function ExportQrButton({ envelope }: { envelope: IngestEnvelope }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    if (!open || !canvasRef.current) return
    const text = encodeEnvelopeForQr(envelope)
    // Temporary debug aid so we can eyeball exactly what the QR carries.
    // eslint-disable-next-line no-console
    console.log('[qr-export]', {
      encodedLength: text.length,
      encodedPrefix: text.slice(0, 48),
      envelope,
      encoded: text,
    })
    QRCodeLib.toCanvas(canvasRef.current, text, { errorCorrectionLevel: 'M', width: 320 }).catch(
      (err: unknown) => {
        // Render failure must not be silent.
        console.error('QR generation failed', err)
      },
    )
  }, [open, envelope])

  return (
    <>
      <button
        className="btn-primary"
        onClick={() => {
          setOpen(true)
        }}
      >
        {t('dataSetDetail.exportQr')}
      </button>
      {open ? (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-10 flex items-center justify-center bg-black/40"
          onClick={() => {
            setOpen(false)
          }}
        >
          <div
            className="rounded-lg bg-white p-6 shadow-xl dark:bg-stone-900"
            onClick={(e) => {
              e.stopPropagation()
            }}
          >
            <canvas ref={canvasRef} />
            <p className="mt-3 text-center text-xs text-stone-500 dark:text-stone-400">
              {t('dataSetDetail.scanAtVisualize')}
            </p>
            <button
              className="btn-secondary mx-auto mt-3 block"
              onClick={() => {
                setOpen(false)
              }}
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      ) : null}
    </>
  )
}
